import type { DocumentDto, DocumentSourceCheckDto, Microsoft365StatusDto, WorkspaceDto } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyZone } from '../../api/hooks.js';
import { formatSize } from '../chat/ChatParts.js';
import { ConsentPanel } from '../microsoft365/files/ConsentPanel.js';
import { changedText, refetchView, sourceState, targetOfPlace, warningText, type RefetchOutcome, type SourceState, type Target } from '../microsoft365/files/model.js';
import { SourceBox } from '../microsoft365/files/SourceLabel.js';
import { documentSize } from './DocumentParts.js';
import { humanTime } from '@dbrain/app-core/format';

/**
 * Belgeler's parts for copies of Microsoft 365 files (MsBelgelerKaynak; DOC-017…): the state in the "Durum" column,
 * the aside's source box with Getiren / Kaynaktaki / Nerede / Boyut, the copy note and "Yeniden al", and the
 * warning a shared place needs ("Anladım"). "Kaynakta değişti" comes from the viewer's own check only. Names are text;
 * nothing here links out of Oppy.
 */

/** the states without an icon draw the list's dot (Hazır green, Okunamadı red) */
const STATE_ICON: Partial<Record<SourceState['kind'], typeof Icons.download>> = {
  importing: Icons.download,
  changed: Icons.regenerate,
  refetch_failed: Icons.warning,
  gone: Icons.info,
};

const STATE_TONE: Record<SourceState['kind'], 'busy' | 'ready' | 'failed' | 'warning' | 'quiet'> = {
  importing: 'busy',
  refetching: 'busy',
  import_failed: 'failed',
  refetch_failed: 'warning',
  changed: 'warning',
  gone: 'quiet',
  unchanged: 'ready',
  updated: 'ready',
};

const STATE_WORD: Record<SourceState['kind'], string> = {
  importing: 'importing',
  refetching: 'refetching',
  import_failed: 'importFailed',
  refetch_failed: 'refetchFailed',
  changed: 'changed',
  gone: 'gone',
  unchanged: 'unchanged',
  updated: 'updated',
};

/** The "Durum" of a copy: İçe aktarılıyor, Alınamadı, Yeniden alınıyor…, Kaynakta değişti, Kaynakta değişiklik yok… */
export function SourceStatus({ state }: { state: SourceState }) {
  const { t } = useTranslation();
  const Icon = STATE_ICON[state.kind];
  const tone = STATE_TONE[state.kind];
  const reason = state.code ? t(`documents.errors.${state.code}`, { defaultValue: t('documents.errors.source_unavailable') }) : undefined;
  return (
    <span className={`doc-status doc-status--${tone} doc-status--source`} title={reason} data-testid="doc-state" data-status={state.kind}>
      {state.kind === 'refetching' ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : Icon ? <Icon size={14} /> : null}
      <span>{t(`documents.sourceState.${STATE_WORD[state.kind]}`)}</span>
      {reason && state.kind === 'refetch_failed' ? <span className="ui-visually-hidden">: {reason}</span> : null}
    </span>
  );
}

/** The aside's source part (MsBelgelerKaynak), above the preview. */
export function DocumentSourcePanel({
  doc,
  check,
  outcome,
  status,
  workspaces,
  onRefetch,
  refetching,
}: {
  doc: DocumentDto;
  check: DocumentSourceCheckDto | undefined;
  outcome: RefetchOutcome | undefined;
  status: Microsoft365StatusDto | undefined;
  workspaces: readonly WorkspaceDto[];
  onRefetch: () => void;
  refetching: boolean;
}) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const hintId = useId();
  const [connecting, setConnecting] = useState(false);
  const source = doc.source;
  if (!source) return null;
  const locale = i18n.language;
  const place = targetOfPlace(doc.scope, doc.workspaceId, workspaces);
  const view = refetchView(doc, status, check);
  const state = sourceState(doc, check, outcome);
  const fetched = source.fetchedAt ? humanTime(source.fetchedAt, locale, t, true, zone) : null;
  const size = [doc.kind === 'text' || doc.kind === 'markdown' ? null : documentSize(doc, t), formatSize(doc.size), doc.estimate ? t(doc.estimate.fitsContext ? 'documents.aside.whole' : 'documents.aside.large') : null]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
  const rows: [string, string][] = [
    [t('documents.aside.fetchedBy'), source.fetchedBy && fetched ? t('documents.aside.fetched', { name: source.fetchedBy, when: fetched }) : (source.fetchedBy ?? fetched ?? '—')],
    [t('documents.aside.atSource'), changedText(source, check, locale, zone, new Date(), t) ?? '—'],
    [t('documents.aside.where'), placeText(place, t)],
    [t('documents.aside.size'), size || '—'],
  ];
  const hint = hintOf(view, t);
  return (
    <div className="doc-source-panel" data-testid="doc-source-panel">
      <SourceBox source={source} />
      <dl className="doc-source-rows">
        {rows.map(([label, value]) => (
          <div key={label} className="doc-source-rows__row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <p className="doc-source-panel__note">{t(`documents.aside.copy.${source.provider}`)}</p>
      {state?.kind === 'refetch_failed' ? (
        <p className="doc-source-panel__line doc-source-panel__line--warning" role="status" data-testid="doc-refetch-failed">
          <Icons.warning size={15} />
          {t('documents.aside.refetchFailed', { reason: t(`documents.errors.${state.code ?? 'source_unavailable'}`, { defaultValue: t('documents.errors.source_unavailable') }) })}
        </p>
      ) : state?.kind === 'unchanged' || state?.kind === 'updated' ? (
        <p className="doc-source-panel__line" role="status" data-testid="doc-refetch-done" data-outcome={state.kind}>
          <Icons.check size={15} />
          {t(state.kind === 'unchanged' ? 'documents.aside.unchanged' : 'documents.aside.updated')}
        </p>
      ) : null}
      {view.kind === 'outlook' ? (
        <p className="doc-source-panel__line" data-testid="doc-refetch-outlook">
          {t('documents.refetch.outlook')}
        </p>
      ) : view.kind === 'hidden' ? null : (
        <div className="doc-source-panel__actions">
          <Button
            size="sm"
            variant="secondary"
            icon={<Icons.regenerate size={15} />}
            busy={view.kind === 'busy' || refetching}
            disabled={view.kind !== 'ready'}
            aria-describedby={hint ? hintId : undefined}
            onClick={onRefetch}
            data-testid="doc-refetch"
          >
            {view.kind === 'busy' ? t('documents.refetch.busy') : t('documents.refetch.action')}
          </Button>
          {hint ? (
            <p className="doc-source-panel__hint" id={hintId} data-testid="doc-refetch-hint" data-reason={view.kind}>
              {hint}
              {view.kind === 'scopes' ? (
                <>
                  {' · '}
                  <button type="button" className="link-button" onClick={() => setConnecting(true)} data-testid="doc-refetch-reconnect">
                    {t('documents.refetch.reconnect')}
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      )}
      {connecting && view.kind === 'scopes' ? <ConsentPanel access="scopes" source={view.source} returnTo="/documents" onRetry={() => setConnecting(false)} /> : null}
    </div>
  );
}

function hintOf(view: ReturnType<typeof refetchView>, t: (key: string) => string): string | null {
  switch (view.kind) {
    case 'connect':
      return t('documents.refetch.connect');
    case 'scopes':
      return t('documents.refetch.scopes');
    case 'off':
      return t('documents.refetch.off');
    case 'switched_off':
      return t('documents.refetch.switchedOff');
    case 'gone':
      return t('documents.refetch.gone');
    case 'forbidden':
      return t('documents.refetch.forbidden');
    default:
      return null;
  }
}

/** "Ekip: Finans · ekipteki herkes görür" */
function placeText(place: Target, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (place.kind === 'team' || place.kind === 'workspace') return t(`documents.aside.place.${place.kind}`, { name: place.name });
  return t(`documents.aside.place.${place.kind}`);
}

/**
 * Paylaş of a copy to a team, a workspace or Herkes: everyone there sees it, also people who cannot see the file in
 * Microsoft 365. The same warning as the picker, with "Anladım"; the server refuses the move without it.
 */
export function ShareWarning({ doc, target, acknowledged, onAcknowledge }: { doc: DocumentDto; target: Target; acknowledged: boolean; onAcknowledge: (on: boolean) => void }) {
  const { t } = useTranslation();
  const text = doc.source ? warningText(target, doc.source.provider, t) : null;
  if (!text) return null;
  return (
    <div className="m365-target__warning doc-share-warning" data-testid="library-share-warning">
      <Icons.warning size={16} />
      <span className="m365-target__warning-body">
        <span>{text}</span>
        <label className="m365-target__ack">
          <input type="checkbox" checked={acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} data-testid="library-share-ack" />
          <span>{t('microsoft365.files.targets.acknowledge')}</span>
        </label>
      </span>
    </div>
  );
}
